import React, { useState } from 'react';
import {
  Button,
  Form,
  Row,
  Col,
  Card,
  Badge,
  Collapse,
} from 'react-bootstrap';
import { FaPlus, FaTrash, FaChevronDown, FaChevronUp } from 'react-icons/fa';
import WorkflowSendEmail from '../../workflow/WorkflowSendEmail';
import WorkflowSendNotification from '../../workflow/WorkflowSendNotification';
import WorkflowSendWhatsApp from '../../workflow/WorkflowSendWhatsApp';
import { standardInputStyles, standardLabelStyles } from '../../../utils/conditionInputStyles';

const INSIGHT_ACTION_TYPES = [
  'Send Notification',
  'Send Email',
  'Send WhatsApp',
];

export default function InsightActionsEditor({
  actions = [],
  objectName,
  onChange,
  disabled = false,
}) {
  const [expandedActions, setExpandedActions] = useState({});

  const setActions = (nextActions) => {
    onChange?.(nextActions);
  };

  const toggleAction = (index) => {
    setExpandedActions((prev) => ({
      ...prev,
      [index]: !prev[index],
    }));
  };

  const addAction = () => {
    const newIndex = actions.length;
    setActions([...actions, { actionType: '', config: {} }]);
    setExpandedActions((prev) => ({ ...prev, [newIndex]: true }));
  };

  const deleteAction = (index) => {
    setActions(actions.filter((_, i) => i !== index));
    setExpandedActions((prev) => {
      const next = {};
      Object.keys(prev).forEach((key) => {
        const i = Number(key);
        if (i < index) next[i] = prev[i];
        else if (i > index) next[i - 1] = prev[i];
      });
      return next;
    });
  };

  const updateAction = (index, field, value) => {
    const next = [...actions];
    next[index] = { ...next[index], [field]: value };
    if (field === 'actionType') {
      next[index].config = {};
    }
    setActions(next);
  };

  const updateActionConfig = (index, key, value) => {
    const next = [...actions];
    const cur = next[index];
    if (!cur) return;
    next[index] = {
      ...cur,
      config: { ...(cur.config || {}), [key]: value },
    };
    setActions(next);
  };

  const patchActionConfig = (index, partial) => {
    if (!partial || typeof partial !== 'object') return;
    const next = [...actions];
    const cur = next[index];
    if (!cur) return;
    next[index] = {
      ...cur,
      config: { ...(cur.config || {}), ...partial },
    };
    setActions(next);
  };

  const renderActionConfigForm = (action, index) => {
    switch (action.actionType) {
      case 'Send Email':
        return (
          <WorkflowSendEmail
            config={action.config || {}}
            onChange={(field, value) => updateActionConfig(index, field, value)}
            onConfigPatch={(partial) => patchActionConfig(index, partial)}
            objectName={objectName}
          />
        );
      case 'Send WhatsApp':
        return (
          <WorkflowSendWhatsApp
            config={action.config || {}}
            onChange={(field, value) => updateActionConfig(index, field, value)}
            objectName={objectName}
          />
        );
      case 'Send Notification':
        return (
          <WorkflowSendNotification
            config={action.config || {}}
            onChange={(field, value) => updateActionConfig(index, field, value)}
            onConfigPatch={(partial) => patchActionConfig(index, partial)}
            objectName={objectName}
          />
        );
      default:
        return (
          <p className="text-muted small mb-0">
            Select an action type to configure recipients and templates.
          </p>
        );
    }
  };

  return (
    <div className="mt-3 pt-3 border-top">
      <div className="d-flex justify-content-between align-items-center mb-2">
        <div>
          <Form.Label className="mb-0 fw-semibold" style={standardLabelStyles}>
            Actions
          </Form.Label>
          <div className="text-muted small">
            Run when this insight threshold is reached (execution coming in a later release).
          </div>
        </div>
        <Button
          size="sm"
          variant="outline-primary"
          onClick={addAction}
          disabled={disabled}
          title={disabled ? 'Select an object first' : 'Add action'}
        >
          <FaPlus className="me-1" />
          Add action
        </Button>
      </div>

      {!actions.length && (
        <p className="text-muted small mb-0">
          {disabled
            ? 'Select an object to configure actions.'
            : 'No actions configured. Add Notification, Email, or WhatsApp.'}
        </p>
      )}

      {actions.map((action, index) => {
        const isExpanded = expandedActions[index] === true;
        return (
          <Card key={`insight-action-${index}`} className="mb-2 border">
            <Card.Header
              className="bg-light py-2"
              style={{ cursor: 'pointer' }}
              onClick={() => toggleAction(index)}
            >
              <div className="d-flex justify-content-between align-items-center">
                <div className="d-flex align-items-center">
                  <span className="me-2">
                    {isExpanded ? <FaChevronDown size={14} /> : <FaChevronUp size={14} />}
                  </span>
                  <span className="small fw-semibold">
                    Action #{index + 1}
                    {action.actionType ? (
                      <Badge bg="secondary" className="ms-2">
                        {action.actionType}
                      </Badge>
                    ) : null}
                  </span>
                </div>
                <Button
                  variant="outline-danger"
                  size="sm"
                  onClick={(e) => {
                    e.stopPropagation();
                    deleteAction(index);
                  }}
                >
                  <FaTrash />
                </Button>
              </div>
            </Card.Header>
            <Collapse in={isExpanded}>
              <div>
                <Card.Body className="py-3">
                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label style={standardLabelStyles}>Action type</Form.Label>
                        <Form.Select
                          value={action.actionType || ''}
                          onChange={(e) => updateAction(index, 'actionType', e.target.value)}
                          style={standardInputStyles}
                        >
                          <option value="">Select action</option>
                          {INSIGHT_ACTION_TYPES.map((type) => (
                            <option key={type} value={type}>
                              {type}
                            </option>
                          ))}
                        </Form.Select>
                      </Form.Group>
                    </Col>
                  </Row>
                  {renderActionConfigForm(action, index)}
                </Card.Body>
              </div>
            </Collapse>
          </Card>
        );
      })}
    </div>
  );
}
